import { Treatment } from "@shuttlerock/api-types/federated";
import { useUser } from "@shuttlerock/auth";
import { useClientAccountContext } from "@shuttlerock/client-account-context";
import {
  ConfigService,
  FEEDBACK_SURVEY_REQUEST_EVENT,
  type FeedbackSurveyName,
  type FeedbackSurveyRequest,
} from "@shuttlerock/configuration";
import { captureException } from "@shuttlerock/observability";
import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";

const HUBSPOT_SCRIPT_ID = "hs-script-loader";
const TRIGGER_SELECTOR_ATTRIBUTE = "data-hubspot-survey-trigger";
const HUBSPOT_SURVEY_CONTEXT_QUERY_KEYS = [
  "src_survey",
  "project_key",
  "survey_context",
  "concept_key",
  "concept_name",
  "concept_number",
  "user_key",
  "client_key",
  "client_name",
  "nps_trigger",
];
const SURVEY_URL_RESTORE_DELAY_MS = 5000;
const EXISTING_HUBSPOT_SCRIPT_FALLBACK_DELAY_MS = 3000;
const HUBSPOT_CTA_RENDER_FALLBACK_DELAY_MS = 1000;
const HUBSPOT_CTA_READY_RETRY_WINDOW_MS = SURVEY_URL_RESTORE_DELAY_MS;
const HUBSPOT_SCRIPT_LOAD_ERROR = "Failed to load HubSpot script";
const HUBSPOT_SCRIPT_LOADED_READY_STATES = new Set(["complete", "loaded"]);

type PendingSurveyRequest = {
  request: FeedbackSurveyRequest;
  requiresAudience: boolean;
};
type HubSpotSurveyConfig = Record<FeedbackSurveyName, { ctaId: string; triggerClasses: string }>;

const createHubSpotSurveyConfig = (
  conceptQualityCtaId: string,
  npsCtaId: string,
): HubSpotSurveyConfig => ({
  concept_quality: {
    ctaId: conceptQualityCtaId,
    triggerClasses: `hs-cta-trigger-button hs-cta-trigger-button-${conceptQualityCtaId}`,
  },
  nps: {
    ctaId: npsCtaId,
    triggerClasses: `hs-cta-trigger-button hs-cta-trigger-button-${npsCtaId}`,
  },
});

/**
 * The viewer's effective treatment set is evaluated server-side and arrives on
 * the authenticated user. An unknown set is an absent one, so every caller
 * fails closed.
 */
const hasTreatment = (treatments: Treatment[] | undefined, treatment: Treatment) =>
  treatments?.includes(treatment) ?? false;

const shouldAttemptNps = (probability: number) => {
  if (probability <= 0) return false;
  if (typeof window === "undefined") return false;

  return Math.random() <= Math.min(probability, 1);
};

const ensureHubSpotQueue = () => {
  window._hsq = window._hsq || [];
  return window._hsq;
};

const removeHubSpotSurveyContextFromUrl = (url: URL) => {
  HUBSPOT_SURVEY_CONTEXT_QUERY_KEYS.forEach((key) => {
    url.searchParams.delete(key);
  });
};

const toRelativeUrl = (url: URL) => `${url.pathname}${url.search}${url.hash}`;

const isHubSpotScriptReady = (script: HTMLElement) => {
  const readyState = (script as HTMLScriptElement & { readyState?: string }).readyState;

  return (
    script.getAttribute("data-loaded") === "true" ||
    HUBSPOT_SCRIPT_LOADED_READY_STATES.has(readyState || "") ||
    window.HubSpotCallsToActions !== undefined
  );
};

const isExpectedHubSpotScript = (
  script: HTMLElement,
  portalId: string,
): script is HTMLScriptElement => {
  return (
    script instanceof HTMLScriptElement && script.src.includes(`js.hs-scripts.com/${portalId}.js`)
  );
};

const hasUnexpectedHubSpotScript = (portalId: string) => {
  const existing = document.getElementById(HUBSPOT_SCRIPT_ID);
  return existing !== null && !isExpectedHubSpotScript(existing, portalId);
};

const loadHubSpotScript = (portalId: string): Promise<void> => {
  window.hsCallsToActionsReady = window.hsCallsToActionsReady || [];

  const existing = document.getElementById(HUBSPOT_SCRIPT_ID);
  if (existing && !isExpectedHubSpotScript(existing, portalId)) {
    return Promise.reject(new Error(HUBSPOT_SCRIPT_LOAD_ERROR));
  }

  if (existing && isHubSpotScriptReady(existing)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    if (existing) {
      const existingScript = existing;
      let fallbackTimer: number | undefined;
      let settled = false;
      function cleanup() {
        existingScript.removeEventListener("load", onLoad);
        existingScript.removeEventListener("error", onError);
        if (fallbackTimer) {
          window.clearTimeout(fallbackTimer);
        }
      }
      function settleLoaded() {
        if (settled) return;
        settled = true;
        existingScript.setAttribute("data-loaded", "true");
        cleanup();
        resolve();
      }
      function settleErrored() {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error(HUBSPOT_SCRIPT_LOAD_ERROR));
      }
      function onLoad() {
        settleLoaded();
      }
      function onError() {
        settleErrored();
      }

      existingScript.addEventListener("load", onLoad, { once: true });
      existingScript.addEventListener("error", onError, { once: true });

      if (isHubSpotScriptReady(existingScript)) {
        settleLoaded();
        return;
      }

      fallbackTimer = window.setTimeout(settleLoaded, EXISTING_HUBSPOT_SCRIPT_FALLBACK_DELAY_MS);
      return;
    }

    const script = document.createElement("script");
    script.id = HUBSPOT_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src = `https://js.hs-scripts.com/${portalId}.js`;
    script.onload = () => {
      script.setAttribute("data-loaded", "true");
      resolve();
    };
    script.onerror = () => reject(new Error(HUBSPOT_SCRIPT_LOAD_ERROR));
    document.head.appendChild(script);
  });
};

const applyHubSpotSurveyParamsToUrl = (
  survey: FeedbackSurveyName,
  params?: FeedbackSurveyRequest["params"],
) => {
  const cleanUrl = new URL(window.location.href);
  removeHubSpotSurveyContextFromUrl(cleanUrl);

  const surveyUrl = new URL(cleanUrl);
  surveyUrl.searchParams.set("src_survey", survey);

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    surveyUrl.searchParams.set(key, String(value));
  });

  window.history.replaceState(window.history.state, "", surveyUrl);
  return {
    path: toRelativeUrl(surveyUrl),
    restoreUrl: cleanUrl,
    surveyUrl,
    state: window.history.state,
  };
};

const clickHubSpotTrigger = (survey: FeedbackSurveyName) => {
  const trigger = document.querySelector<HTMLButtonElement>(
    `[${TRIGGER_SELECTOR_ATTRIBUTE}="${survey}"]`,
  );
  trigger?.click();
};

const removeHubSpotReadyCallback = (callback: () => void) => {
  const readyCallbacks = window.hsCallsToActionsReady;
  if (!readyCallbacks) return;

  const callbackIndex = readyCallbacks.indexOf(callback);
  if (callbackIndex >= 0) {
    readyCallbacks.splice(callbackIndex, 1);
  }
};

const refreshAndClickHubSpotTrigger = (survey: FeedbackSurveyName, ctaId: string) => {
  let apiReady = false;
  let cancelled = false;
  let apiReadyFallbackTimer: number | undefined;
  let cleanupReadyListener: (() => void) | undefined;

  const cancel = () => {
    if (cancelled) return;
    cancelled = true;
    if (apiReadyFallbackTimer) {
      window.clearTimeout(apiReadyFallbackTimer);
    }
    removeHubSpotReadyCallback(refreshAndClick);
    cleanupReadyListener?.();
  };

  const refreshAndClick = () => {
    if (apiReady || cancelled) return;
    apiReady = true;
    if (apiReadyFallbackTimer) {
      window.clearTimeout(apiReadyFallbackTimer);
    }

    const api = window.HubSpotCallsToActions;
    const refresh = api?.refresh;
    if (!api || !refresh) {
      clickHubSpotTrigger(survey);
      return;
    }
    const readyApi = api;

    let acceptsReadyEvent = true;
    let readyEventHandled = false;
    let renderFallbackTimer: number | undefined;
    let readyRetryTimer: number | undefined;

    function cleanup() {
      acceptsReadyEvent = false;
      if (renderFallbackTimer) {
        window.clearTimeout(renderFallbackTimer);
      }
      if (readyRetryTimer) {
        window.clearTimeout(readyRetryTimer);
      }
      readyApi.off?.("onCallToActionReady", onReady);
    }
    cleanupReadyListener = cleanup;
    function onReady({ id }: { id: string | number }) {
      if (!acceptsReadyEvent || readyEventHandled || String(id) !== ctaId) return;
      readyEventHandled = true;
      cleanup();
      clickHubSpotTrigger(survey);
    }

    readyApi.on?.("onCallToActionReady", onReady);
    renderFallbackTimer = window.setTimeout(() => {
      if (!readyEventHandled) {
        clickHubSpotTrigger(survey);
      }
    }, HUBSPOT_CTA_RENDER_FALLBACK_DELAY_MS);
    readyRetryTimer = window.setTimeout(cleanup, HUBSPOT_CTA_READY_RETRY_WINDOW_MS);
    refresh.call(readyApi);
  };

  if (window.HubSpotCallsToActions) {
    refreshAndClick();
    return cancel;
  }

  window.hsCallsToActionsReady = window.hsCallsToActionsReady || [];
  window.hsCallsToActionsReady.push(refreshAndClick);
  apiReadyFallbackTimer = window.setTimeout(() => {
    if (!apiReady && !cancelled) {
      clickHubSpotTrigger(survey);
    }
  }, HUBSPOT_CTA_RENDER_FALLBACK_DELAY_MS);
  return cancel;
};

const restoreUrl = (cleanUrl: URL, surveyUrl: URL, state: unknown) => {
  const currentUrl = new URL(window.location.href);

  if (toRelativeUrl(currentUrl) === toRelativeUrl(surveyUrl)) {
    window.history.replaceState(state, "", cleanUrl);
    return;
  }

  removeHubSpotSurveyContextFromUrl(currentUrl);
  window.history.replaceState(window.history.state, "", currentUrl);
};

const hiddenTriggerButtonStyle: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
  border: 0,
};

export const HubSpotTracking = () => {
  const { user } = useUser();
  const { defaultAccount } = useClientAccountContext();
  const location = useLocation();
  const portalId = ConfigService.HUBSPOT_TRACKING_PORTAL_ID();
  const conceptQualityCtaId = ConfigService.HUBSPOT_CONCEPT_QUALITY_CTA_ID();
  const npsCtaId = ConfigService.HUBSPOT_NPS_CTA_ID();
  const npsProbability = ConfigService.HUBSPOT_NPS_TRIGGER_PROBABILITY();
  const hasScriptConflict = Boolean(portalId) && hasUnexpectedHubSpotScript(portalId);
  const loadedRef = useRef(false);
  const npsAttemptedRef = useRef(false);
  const manualNpsAttemptedRef = useRef(false);
  const manualConceptSurveyAttemptedRef = useRef(false);
  const initialPathQueuedRef = useRef(false);
  const pendingSurveyRequests = useRef<PendingSurveyRequest[]>([]);
  const restoreTimerRef = useRef<number | undefined>(undefined);
  const cancelActiveSurveyTriggerRef = useRef<(() => void) | undefined>(undefined);

  // What HubSpot needs before audience is even a question: a portal, an
  // uncontested script, and an identity to attribute the session to.
  const hubSpotConfigured = Boolean(portalId) && !hasScriptConflict && !!user?.email;
  const treatments = user?.treatments;
  // The transport itself is a treatment, so a suppressed viewer loads no script
  // and is never identified to HubSpot.
  const hubSpotEnabled = hubSpotConfigured && hasTreatment(treatments, Treatment.CrmTracking);
  // Each survey carries its own treatment; they share no audience, so either can
  // be switched off without the other. Memoised over the two booleans rather
  // than the treatment array, so the lookup keeps a stable identity for the
  // callbacks and effects below.
  const conceptQualityEnabled =
    hubSpotEnabled && hasTreatment(treatments, Treatment.CsatConceptQuality);
  const npsEnabled = hubSpotEnabled && hasTreatment(treatments, Treatment.NpsLottery);
  const surveyEnabled = useMemo<Record<FeedbackSurveyName, boolean>>(
    () => ({ concept_quality: conceptQualityEnabled, nps: npsEnabled }),
    [conceptQualityEnabled, npsEnabled],
  );
  const requestedSurvey = new URLSearchParams(location.search).get("src_survey");
  // The manual diagnostic bypasses the *survey* treatment, not the transport:
  // it still requires `CRM_TRACKING`, so suppressing that treatment remains a
  // guarantee that a viewer loads no HubSpot script rather than a default a
  // query parameter can undo. Both of its stated uses — explicit testing, and
  // forcing a survey for a specific person — target viewers who have the
  // default-on transport.
  const manualConceptDiagnosticRequested = requestedSurvey === "concept_quality";

  const surveyConfig = useMemo(
    () => createHubSpotSurveyConfig(conceptQualityCtaId, npsCtaId),
    [conceptQualityCtaId, npsCtaId],
  );

  const identifyTraits = useMemo(
    () => ({
      email: user?.email,
      id: user?.key,
      userKey: user?.key,
      name: user?.fullName,
      firstName: user?.firstName,
      lastName: user?.lastName,
      clientKey: defaultAccount?.key,
      clientName: defaultAccount?.name,
    }),
    [defaultAccount?.key, defaultAccount?.name, user],
  );

  const showHubSpotSurvey = useCallback(
    (request: FeedbackSurveyRequest, requiresAudience = true) => {
      if (!hubSpotEnabled) return;
      if (requiresAudience && !surveyEnabled[request.survey]) return;

      const { ctaId } = surveyConfig[request.survey];
      if (!ctaId) return;

      if (!loadedRef.current) {
        pendingSurveyRequests.current.push({ request, requiresAudience });
        return;
      }

      if (restoreTimerRef.current) {
        window.clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = undefined;
      }
      cancelActiveSurveyTriggerRef.current?.();
      cancelActiveSurveyTriggerRef.current = undefined;

      const {
        path,
        restoreUrl: cleanUrl,
        surveyUrl,
        state,
      } = applyHubSpotSurveyParamsToUrl(request.survey, {
        ...request.params,
        user_key: user?.key,
        client_key: defaultAccount?.key,
      });

      ensureHubSpotQueue().push(["setPath", path], ["trackPageView"]);
      cancelActiveSurveyTriggerRef.current = refreshAndClickHubSpotTrigger(request.survey, ctaId);

      restoreTimerRef.current = window.setTimeout(() => {
        cancelActiveSurveyTriggerRef.current?.();
        cancelActiveSurveyTriggerRef.current = undefined;
        restoreTimerRef.current = undefined;
        restoreUrl(cleanUrl, surveyUrl, state);
      }, SURVEY_URL_RESTORE_DELAY_MS);
    },
    [defaultAccount?.key, hubSpotEnabled, surveyConfig, surveyEnabled, user?.key],
  );

  useEffect(
    () => () => {
      cancelActiveSurveyTriggerRef.current?.();
      cancelActiveSurveyTriggerRef.current = undefined;
      if (restoreTimerRef.current) {
        window.clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = undefined;
      }
    },
    [],
  );

  useEffect(() => {
    if (!hasScriptConflict) return;
    captureException(new Error(HUBSPOT_SCRIPT_LOAD_ERROR));
  }, [hasScriptConflict]);

  useEffect(() => {
    if (!portalId) return;

    if (!hubSpotEnabled) return;

    ensureHubSpotQueue();
    loadHubSpotScript(portalId)
      .then(() => {
        loadedRef.current = true;
        pendingSurveyRequests.current
          .splice(0)
          .forEach(({ request, requiresAudience }) => showHubSpotSurvey(request, requiresAudience));
      })
      .catch(captureException);
  }, [portalId, showHubSpotSurvey, hubSpotEnabled]);

  useEffect(() => {
    if (!hubSpotEnabled) return;
    ensureHubSpotQueue().push(["identify", identifyTraits]);
  }, [identifyTraits, hubSpotEnabled]);

  useEffect(() => {
    if (!hubSpotEnabled) {
      cancelActiveSurveyTriggerRef.current?.();
      cancelActiveSurveyTriggerRef.current = undefined;
      pendingSurveyRequests.current = [];
      return;
    }

    if (initialPathQueuedRef.current) {
      cancelActiveSurveyTriggerRef.current?.();
      cancelActiveSurveyTriggerRef.current = undefined;
    }

    ensureHubSpotQueue().push([
      "setPath",
      `${location.pathname}${location.search}${location.hash}`,
    ]);

    if (!initialPathQueuedRef.current) {
      initialPathQueuedRef.current = true;
      return;
    }

    if (!loadedRef.current) return;

    ensureHubSpotQueue().push(["trackPageView"]);
  }, [location.hash, location.pathname, location.search, hubSpotEnabled]);

  useEffect(() => {
    if (!hubSpotEnabled) return;

    const handler = (event: Event) => {
      showHubSpotSurvey((event as CustomEvent<FeedbackSurveyRequest>).detail);
    };

    window.addEventListener(FEEDBACK_SURVEY_REQUEST_EVENT, handler);
    return () => window.removeEventListener(FEEDBACK_SURVEY_REQUEST_EVENT, handler);
  }, [showHubSpotSurvey, hubSpotEnabled]);

  useEffect(() => {
    if (!manualConceptDiagnosticRequested) return;
    if (manualConceptSurveyAttemptedRef.current) return;

    manualConceptSurveyAttemptedRef.current = true;
    showHubSpotSurvey({ survey: "concept_quality" }, false);
  }, [manualConceptDiagnosticRequested, showHubSpotSurvey]);

  useEffect(() => {
    if (!npsEnabled) return;

    if (requestedSurvey === "concept_quality") {
      npsAttemptedRef.current = true;
      return;
    }

    if (requestedSurvey === "nps") {
      if (manualNpsAttemptedRef.current) return;
      manualNpsAttemptedRef.current = true;
      npsAttemptedRef.current = true;
      showHubSpotSurvey({ survey: "nps" });
      return;
    }

    if (npsAttemptedRef.current) return;
    npsAttemptedRef.current = true;

    if (!shouldAttemptNps(npsProbability)) {
      return;
    }

    showHubSpotSurvey({
      survey: "nps",
      params: { nps_trigger: "annual_random" },
    });
  }, [npsEnabled, npsProbability, requestedSurvey, showHubSpotSurvey]);

  if (!hubSpotEnabled) return null;

  return (
    <>
      {Object.entries(surveyConfig).map(([survey, { ctaId, triggerClasses }]) => {
        const name = survey as FeedbackSurveyName;
        const rendered =
          surveyEnabled[name] || (name === "concept_quality" && manualConceptDiagnosticRequested);

        return ctaId && rendered ? (
          <button
            key={survey}
            type="button"
            className={triggerClasses}
            tabIndex={-1}
            aria-hidden="true"
            style={hiddenTriggerButtonStyle}
            data-hubspot-survey-trigger={survey}
          />
        ) : null;
      })}
    </>
  );
};
